/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Grid, Typography, breakpoints } from '@coursera/cds-core';

import RelatedArticlesCard from 'bundles/shared-articles/components/RelatedArticlesCard';
import RelatedArticlesCardPlaceholder from 'bundles/shared-articles/components/RelatedArticlesCardPlaceholder';
import { NUMBER_OF_RELATED_ARTICLES_TO_DISPLAY, PAGE_TYPE } from 'bundles/unified-seo-content-common/constants';
import useEstimatedReadTimeData from 'bundles/unified-seo-content-common/hooks/useEstimatedReadTimeData';
import useRelatedArticlesData from 'bundles/unified-seo-content-common/hooks/useRelatedArticlesData';
import type { ArticleMetadata } from 'bundles/unified-seo-content-common/hooks/useRelatedArticlesData';
import { getPageLink } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/articles';

type Props = {
  articleMetadata: ArticleMetadata;
};

const styles = {
  fullWidthContainer: css`
    margin: 50px;
    padding: var(--cds-spacing-200) 0;
    width: 100vw;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
  `,
  relatedArticlesContainer: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 4px;
    max-width: 1022px;
    margin: 0 auto;
    padding: var(--cds-spacing-300) var(--cds-spacing-400);

    ul {
      list-style-type: none;
      padding: 0;
      ${breakpoints.down('xs')} {
        li :nth-of-type(n + 4) {
          display: none;
        }
      }
    }

    @media (max-width: 1060px) {
      max-width: 95%;
      margin: 0 auto;
    }

    ${breakpoints.down('xs')} {
      max-width: 90%;
      margin: 0 auto;
    }
  `,
  sectionTitle: css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

type CardProps = {
  slug: string;
  title: string;
  localizedCountryCode: string;
  localizedLanguageCode: string;
  publishedAt: string;
};

export const RelatedArticlesCardWithEstimatedReadTime = ({
  localizedCountryCode,
  localizedLanguageCode,
  publishedAt,
  slug,
  title,
}: CardProps) => {
  const { estimatedReadTimeLabel } = useEstimatedReadTimeData({ slug });
  const articleUrl = getPageLink({
    slug,
    localizedCountryCode,
    localizedLanguageCode,
    trackingQueryParam: 'relatedArticlesCard',
    pageType: PAGE_TYPE.content,
  });

  return (
    <RelatedArticlesCard
      title={title}
      publishedDate={publishedAt}
      key={slug}
      articleUrl={articleUrl}
      estimatedReadTimeLabel={estimatedReadTimeLabel}
    />
  );
};

export const AutoGeneratedRelatedArticles = ({ articleMetadata }: Props) => {
  const { relatedArticles, loadingRelatedArticles } = useRelatedArticlesData(articleMetadata);

  if (!loadingRelatedArticles && (!relatedArticles || !relatedArticles.length)) return null;

  return (
    <div css={styles.fullWidthContainer}>
      <div css={styles.relatedArticlesContainer}>
        <Typography css={styles.sectionTitle} variant="h2semibold">
          {_t('Keep reading')}
        </Typography>
        <Grid container spacing={24} component="ul">
          {loadingRelatedArticles
            ? new Array(NUMBER_OF_RELATED_ARTICLES_TO_DISPLAY)
                .fill('placeholder')
                .map((el: string, index) => <RelatedArticlesCardPlaceholder key={`${el}${index}`} />)
            : relatedArticles.map((article) => {
                const { slug, title, localizedCountryCode, localizedLanguageCode, publishedAt } = article;
                return (
                  <RelatedArticlesCardWithEstimatedReadTime
                    key={slug}
                    slug={slug}
                    title={title}
                    localizedCountryCode={localizedCountryCode}
                    localizedLanguageCode={localizedLanguageCode}
                    publishedAt={publishedAt}
                  />
                );
              })}
        </Grid>
      </div>
    </div>
  );
};

export default AutoGeneratedRelatedArticles;
